// Enhanced token management for session handling

export interface TokenData {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: any;
}

export class TokenManager {
  private static readonly ACCESS_TOKEN_KEY = 'access_token';
  private static readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private static readonly USER_KEY = 'user';
  private static readonly EXPIRES_AT_KEY = 'expires_at';
  private static readonly REMEMBER_ME_KEY = 'remember_me';

  private refreshPromise: Promise<string> | null = null;

  /**
   * Store tokens with expiration
   */
  setTokens(data: TokenData, rememberMe: boolean = false): void {
    const storage = rememberMe ? localStorage : sessionStorage;
    
    storage.setItem(TokenManager.ACCESS_TOKEN_KEY, data.access_token);
    storage.setItem(TokenManager.REFRESH_TOKEN_KEY, data.refresh_token);
    storage.setItem(TokenManager.EXPIRES_AT_KEY, data.expires_at.toString());
    storage.setItem(TokenManager.USER_KEY, JSON.stringify(data.user));
    
    // Store preference
    localStorage.setItem(TokenManager.REMEMBER_ME_KEY, rememberMe.toString());
    
    // Also store in the other storage for consistency during session
    if (rememberMe) {
      sessionStorage.setItem(TokenManager.ACCESS_TOKEN_KEY, data.access_token);
    }
  }

  /**
   * Get access token from storage
   */
  getAccessToken(): string | null {
    // Check session storage first, then localStorage
    return sessionStorage.getItem(TokenManager.ACCESS_TOKEN_KEY) || 
           localStorage.getItem(TokenManager.ACCESS_TOKEN_KEY);
  }

  /**
   * Get refresh token from storage
   */
  getRefreshToken(): string | null {
    // Check session storage first, then localStorage
    return sessionStorage.getItem(TokenManager.REFRESH_TOKEN_KEY) || 
           localStorage.getItem(TokenManager.REFRESH_TOKEN_KEY);
  }

  /**
   * Get user data from storage
   */
  getUser(): any | null {
    const userStr = sessionStorage.getItem(TokenManager.USER_KEY) || 
                   localStorage.getItem(TokenManager.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Get token expiration time
   */
  getExpiresAt(): number | null {
    const expiresStr = sessionStorage.getItem(TokenManager.EXPIRES_AT_KEY) || 
                      localStorage.getItem(TokenManager.EXPIRES_AT_KEY);
    return expiresStr ? parseInt(expiresStr, 10) : null;
  }

  /**
   * Check if user chose to be remembered
   */
  isRemembered(): boolean {
    return localStorage.getItem(TokenManager.REMEMBER_ME_KEY) === 'true';
  }

  /**
   * Check if token is expired or will expire soon (within 5 minutes)
   */
  isTokenExpired(bufferMinutes: number = 5): boolean {
    const expiresAt = this.getExpiresAt();
    if (!expiresAt) return true;
    
    const now = Date.now();
    const buffer = bufferMinutes * 60 * 1000; // Convert to milliseconds
    return now >= (expiresAt - buffer);
  }

  /**
   * Check if user has valid authentication
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired();
  }

  /**
   * Update access token after refresh
   */
  updateAccessToken(accessToken: string, expiresIn: number): void {
    const expiresAt = Date.now() + (expiresIn * 1000);
    
    // Update in both storages for consistency
    const storage = this.isRemembered() ? localStorage : sessionStorage;
    storage.setItem(TokenManager.ACCESS_TOKEN_KEY, accessToken);
    storage.setItem(TokenManager.EXPIRES_AT_KEY, expiresAt.toString());
    
    sessionStorage.setItem(TokenManager.ACCESS_TOKEN_KEY, accessToken);
  }

  /**
   * Clear all tokens and user data
   */
  clearTokens(): void {
    // Clear from both storages
    [localStorage, sessionStorage].forEach(storage => {
      storage.removeItem(TokenManager.ACCESS_TOKEN_KEY);
      storage.removeItem(TokenManager.REFRESH_TOKEN_KEY);
      storage.removeItem(TokenManager.USER_KEY);
      storage.removeItem(TokenManager.EXPIRES_AT_KEY);
    });
    localStorage.removeItem(TokenManager.REMEMBER_ME_KEY);
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(refreshFn: (refreshToken: string) => Promise<{ access_token: string; expires_in: number }>): Promise<string> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._performRefresh(refreshFn);
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async _performRefresh(refreshFn: (refreshToken: string) => Promise<{ access_token: string; expires_in: number }>): Promise<string> {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await refreshFn(refreshToken);
      this.updateAccessToken(response.access_token, response.expires_in);
      return response.access_token;
    } catch (error) {
      // If refresh fails, clear all tokens
      this.clearTokens();
      throw error;
    }
  }

  /**
   * Get token with automatic refresh if needed
   */
  async getValidToken(refreshFn: (refreshToken: string) => Promise<{ access_token: string; expires_in: number }>): Promise<string | null> {
    const currentToken = this.getAccessToken();
    
    if (!currentToken) {
      return null;
    }

    // If token is not expired, return it
    if (!this.isTokenExpired()) {
      return currentToken;
    }

    // If we have a refresh token, try to refresh
    if (this.getRefreshToken()) {
      try {
        return await this.refreshAccessToken(refreshFn);
      } catch (error) {
        console.error('Token refresh failed:', error);
        return null;
      }
    }

    return null;
  }

  /**
   * Schedule automatic token refresh
   */
  scheduleTokenRefresh(refreshFn: (refreshToken: string) => Promise<{ access_token: string; expires_in: number }>): number | null {
    const expiresAt = this.getExpiresAt();
    if (!expiresAt) return null;

    // Schedule refresh 5 minutes before expiration
    const refreshTime = expiresAt - Date.now() - (5 * 60 * 1000);
    
    if (refreshTime <= 0) {
      // Token already expired or expires very soon, refresh immediately
      this.refreshAccessToken(refreshFn).catch(console.error);
      return null;
    }

    return window.setTimeout(() => {
      this.refreshAccessToken(refreshFn).catch(console.error);
    }, refreshTime);
  }
}

export const tokenManager = new TokenManager();